import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import {
  ChartLine,
  CurrencyDollar,
  Receipt,
  CalendarBlank,
  TrendUp,
  Money,
} from "@phosphor-icons/react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { COLORS } from "./shared";

export const TodaySnapshot = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    const dateStr = format(selectedDate, "yyyy-MM-dd");
    axios
      .get(`${API}/reports/daily?date=${dateStr}`)
      .then((r) => {
        if (!ignore) setReport(r.data);
      })
      .catch(() => toast.error("Failed to fetch snapshot"))
      .finally(() => {
        if (!ignore) setLoading(false);
      });
    return () => {
      ignore = true;
    };
  }, [selectedDate]);

  const categoryData = report?.category_breakdown
    ? Object.entries(report.category_breakdown).map(([name, value]) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        value: Math.round(value * 100) / 100,
      }))
    : [];

  const paymentData = report?.payment_breakdown
    ? Object.entries(report.payment_breakdown).map(([name, value]) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        value: Math.round(value * 100) / 100,
      }))
    : [];

  return (
    <Card data-testid="snapshot-card">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <ChartLine size={20} /> Today&apos;s Snapshot
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Live sales summary for {format(selectedDate, "MMMM d, yyyy")}.
            </p>
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="snapshot-date-btn">
                <CalendarBlank size={16} />
                {format(selectedDate, "MMM d, yyyy")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(d) => d && setSelectedDate(d)}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : report ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <SnapshotTile
                icon={<CurrencyDollar size={22} weight="fill" />}
                label="Total Sales"
                value={`$${report.total_sales.toFixed(2)}`}
                color="text-primary"
                testId="snap-total-sales"
              />
              <SnapshotTile
                icon={<Receipt size={22} weight="fill" />}
                label="Orders"
                value={report.total_orders}
                color="text-accent"
                testId="snap-total-orders"
              />
              <SnapshotTile
                icon={<TrendUp size={22} weight="fill" />}
                label="Avg Order"
                value={`$${report.average_order_value.toFixed(2)}`}
                color="text-[#D29856]"
                testId="snap-avg-order"
              />
              <SnapshotTile
                icon={<Money size={22} weight="fill" />}
                label="Tips"
                value={`$${report.total_tips.toFixed(2)}`}
                color="text-green-600"
                testId="snap-tips"
              />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="rounded-lg border border-border p-3">
                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
                  Sales by Category
                </div>
                {categoryData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={categoryData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2DED7" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v) => [`$${v.toFixed(2)}`, "Sales"]} />
                      <Bar dataKey="value" fill="#B85D43" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[220px] flex items-center justify-center text-sm text-muted-foreground">
                    No sales for this date
                  </div>
                )}
              </div>
              <div className="rounded-lg border border-border p-3">
                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
                  Payment Methods
                </div>
                {paymentData.length > 0 && paymentData.some((d) => d.value > 0) ? (
                  <>
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie
                          data={paymentData}
                          cx="50%"
                          cy="50%"
                          innerRadius={45}
                          outerRadius={80}
                          paddingAngle={4}
                          dataKey="value"
                        >
                          {paymentData.map((_, i) => (
                            <Cell key={i} fill={COLORS[i % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(v) => [`$${v.toFixed(2)}`, ""]} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="flex flex-wrap justify-center gap-3 mt-2">
                      {paymentData.map((e, i) => (
                        <div key={e.name} className="flex items-center gap-1.5 text-xs">
                          <div
                            className="w-2.5 h-2.5 rounded-full"
                            style={{ backgroundColor: COLORS[i % COLORS.length] }}
                          />
                          <span className="text-muted-foreground">
                            {e.name}: ${e.value.toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="h-[220px] flex items-center justify-center text-sm text-muted-foreground">
                    No payment data
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-12 text-muted-foreground">No data for this date</div>
        )}
      </CardContent>
    </Card>
  );
};

const SnapshotTile = ({ icon, label, value, color, testId }) => (
  <div className="rounded-lg border border-border p-3" data-testid={testId}>
    <div className="flex items-start justify-between">
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className="font-mono text-2xl font-semibold mt-1">{value}</div>
      </div>
      <div className={`${color} p-1.5 bg-secondary rounded-md shrink-0`}>{icon}</div>
    </div>
  </div>
);
